import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Clock, ChevronUp, ChevronDown } from 'lucide-react';

// ── Types ───────────────────────────────────────────────────────────────────

interface DateTimePickerProps {
  /** Stored value as YYYY-MM-DD or YYYY-MM-DDTHH:MM */
  value: string;
  onChange: (value: string) => void;
  /** Max date in YYYY-MM-DD format — validated in JS only, NOT set as HTML attr */
  maxDate?: string;
  placeholder?: string;
  /** 'input' (default) shows bordered input-style trigger; 'inline' shows clickable text */
  variant?: 'input' | 'inline';
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Valid YYYY-MM-DD pattern */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseValue(value: string): { date: string; hour: number; minute: number; period: 'AM' | 'PM' } {
  const date = value ? value.split('T')[0] : '';
  if (!value || !value.includes('T')) {
    return { date, hour: 9, minute: 0, period: 'AM' };
  }
  const [h, m] = value.split('T')[1].split(':').map(Number);
  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return { date, hour, minute: m, period };
}

function to24Hour(hour: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return hour === 12 ? 0 : hour;
  return hour === 12 ? 12 : hour + 12;
}

function buildValue(date: string, hour: number, minute: number, period: 'AM' | 'PM'): string {
  if (!date) return '';
  const h24 = to24Hour(hour, period);
  return `${date}T${h24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

function formatDisplay(date: string, hour: number, minute: number, period: 'AM' | 'PM'): string {
  const [y, m, d] = date.split('-').map(Number);
  const dateStr = new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return `${dateStr} at ${hour}:${minute.toString().padStart(2, '0')} ${period}`;
}

function snapMinute(current: number, direction: 'up' | 'down'): number {
  const step = 15;
  if (direction === 'up') {
    const next = Math.ceil((current + 1) / step) * step;
    return next >= 60 ? 0 : next;
  }
  const prev = Math.floor((current - 1) / step) * step;
  return prev < 0 ? 45 : prev;
}

function clampHour(v: number): number {
  if (v < 1) return 1;
  if (v > 12) return 12;
  return v;
}

function clampMinute(v: number): number {
  if (v < 0) return 0;
  if (v > 59) return 59;
  return v;
}

// ── Editable Number ─────────────────────────────────────────────────────────

function EditableNumber({
  value,
  onChange,
  min,
  max,
  onIncrement,
  onDecrement,
  pad = 1,
  label,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  onIncrement: () => void;
  onDecrement: () => void;
  pad?: number;
  label: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setDraft(String(value));
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEdit = () => {
    setEditing(false);
    const parsed = parseInt(draft, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onChange(clamped);
    }
  };

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[10px] text-slate uppercase tracking-wider font-medium">{label}</span>
      <button
        type="button"
        onClick={onIncrement}
        className="w-10 h-7 flex items-center justify-center rounded-md text-slate
                   hover:bg-plum/10 hover:text-plum active:bg-plum/20 transition-colors"
        aria-label={`Increase ${label}`}
      >
        <ChevronUp size={16} />
      </button>

      {editing ? (
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => { if (e.key === 'Enter') commitEdit(); }}
          className="w-12 text-center font-mono text-[20px] font-semibold text-midnight
                     leading-none tabular-nums bg-plum/5 border border-plum rounded-md
                     outline-none py-0.5
                     [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
          style={{ MozAppearance: 'textfield' }}
        />
      ) : (
        <button
          type="button"
          onClick={startEditing}
          className="font-mono text-[20px] font-semibold text-midnight leading-none tabular-nums
                     select-none cursor-text hover:text-plum transition-colors rounded-md
                     px-1 py-0.5 hover:bg-plum/5"
          aria-label={`Edit ${label}`}
        >
          {String(value).padStart(pad, '0')}
        </button>
      )}

      <button
        type="button"
        onClick={onDecrement}
        className="w-10 h-7 flex items-center justify-center rounded-md text-slate
                   hover:bg-plum/10 hover:text-plum active:bg-plum/20 transition-colors"
        aria-label={`Decrease ${label}`}
      >
        <ChevronDown size={16} />
      </button>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export function DateTimePicker({
  value,
  onChange,
  maxDate,
  placeholder = 'Select date & time',
  variant = 'input',
}: DateTimePickerProps) {
  const parsed = parseValue(value);
  const [date, setDate] = useState(parsed.date);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState<'AM' | 'PM'>(parsed.period);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Only validate maxDate when it's a valid YYYY-MM-DD string
  const safeMaxDate = maxDate && DATE_RE.test(maxDate) ? maxDate : undefined;

  // Sync from prop when value changes externally
  useEffect(() => {
    const p = parseValue(value);
    setDate(p.date);
    setHour(p.hour);
    setMinute(p.minute);
    setPeriod(p.period);
  }, [value]);

  // Position the portal dropdown relative to the trigger button
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 340;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    const width = Math.min(rect.width, 320);

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: viewportHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  // Update position when opening and on scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const emitChange = useCallback((d: string, h: number, m: number, p: 'AM' | 'PM') => {
    onChange(buildValue(d, h, m, p));
  }, [onChange]);

  const handleDateChange = (newDate: string) => {
    // Validate maxDate in JS only — never set as HTML max attribute
    if (newDate && safeMaxDate && newDate > safeMaxDate) return;
    setDate(newDate);
    if (newDate) {
      emitChange(newDate, hour, minute, period);
    } else {
      onChange('');
    }
  };

  const handleHourChange = (h: number) => {
    const clamped = clampHour(h);
    setHour(clamped);
    if (date) emitChange(date, clamped, minute, period);
  };

  const handleMinuteChange = (m: number) => {
    const clamped = clampMinute(m);
    setMinute(clamped);
    if (date) emitChange(date, hour, clamped, period);
  };

  const handlePeriodToggle = () => {
    const next = period === 'AM' ? 'PM' : 'AM';
    setPeriod(next);
    if (date) emitChange(date, hour, minute, next);
  };

  const formatted = date ? formatDisplay(date, hour, minute, period) : null;

  return (
    <>
      {/* Trigger button */}
      {variant === 'inline' ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 text-plum font-medium hover:text-rose
                     transition-colors duration-150 active:opacity-70"
        >
          {formatted ? (
            <span className="text-[12px]">{formatted}</span>
          ) : (
            <span className="text-[12px] italic text-slate">{placeholder}</span>
          )}
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(o => !o)}
          className={`w-full h-11 rounded-lg border bg-white flex items-center px-3 gap-2 text-left
                      transition-colors duration-150 min-w-0
                      ${open ? 'border-plum ring-2 ring-plum/20' : 'border-stone hover:border-plum/40'}`}
        >
          <Calendar size={16} className="shrink-0 text-slate" />
          <span className={`text-small flex-1 truncate min-w-0 ${formatted ? 'text-midnight' : 'text-slate'}`}>
            {formatted ?? placeholder}
          </span>
        </button>
      )}

      {/* Dropdown panel — rendered in a portal so it's never clipped by parent overflow */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="bg-white border border-stone rounded-xl shadow-brand-lg p-4
                     flex flex-col gap-3"
        >
          {/* Date input — NO max attr, NO appearance-none (both break iOS native picker) */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={e => handleDateChange(e.target.value)}
              onInput={e => handleDateChange((e.target as HTMLInputElement).value)}
              className="block w-full h-11 rounded-lg border border-stone bg-white
                         text-sm text-midnight text-center
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
            {safeMaxDate && (
              <p className="text-[10px] text-slate mt-1">
                On or before {new Date(safeMaxDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>

          {/* Time selector */}
          {date && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                <Clock size={12} className="inline mr-1 -mt-0.5" />
                Time
              </label>
              <div className="flex items-center justify-center gap-2 py-2">
                <EditableNumber
                  value={hour}
                  onChange={handleHourChange}
                  min={1}
                  max={12}
                  onIncrement={() => handleHourChange(hour >= 12 ? 1 : hour + 1)}
                  onDecrement={() => handleHourChange(hour <= 1 ? 12 : hour - 1)}
                  label="Hr"
                />
                <span className="font-mono text-[20px] font-semibold text-slate select-none mt-4">:</span>
                <EditableNumber
                  value={minute}
                  onChange={handleMinuteChange}
                  min={0}
                  max={59}
                  onIncrement={() => handleMinuteChange(snapMinute(minute, 'up'))}
                  onDecrement={() => handleMinuteChange(snapMinute(minute, 'down'))}
                  pad={2}
                  label="Min"
                />
                <button
                  type="button"
                  onClick={handlePeriodToggle}
                  className="ml-1 mt-4 h-9 px-3 rounded-lg border border-stone text-[13px] font-semibold
                             text-midnight hover:border-plum hover:text-plum active:bg-plum/5
                             transition-colors duration-150 select-none"
                >
                  {period}
                </button>
              </div>
            </div>
          )}

          {/* Done button */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="btn-primary w-full text-[13px]"
          >
            Done
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
